import { type RefObject, useEffect, useLayoutEffect, useRef } from 'react'

import { sigEase, sigMs, signatureMoves } from '../lib/signature/motion'

type Kind = 'underline' | 'segmented'
type Rect = { left: number; right: number; top: number; bottom: number }
type Position = { index: number; rect: Rect }
type Snapshot = { value: string; index: number; rect: Rect; element: HTMLElement }
type Stop = (cancel: boolean) => void

function directionOf(group: HTMLElement) {
  const declaredDirection = group.closest('[dir]')?.getAttribute('dir')
  return declaredDirection
    ? declaredDirection === 'rtl'
    : getComputedStyle(group).direction === 'rtl' || document.documentElement.dir === 'rtl'
}

function relativeRect(
  group: HTMLElement,
  element: HTMLElement,
  rtl: boolean,
  width: number,
  viewportWidth: number,
): Rect {
  const groupRect = group.getBoundingClientRect()
  const elementRect = element.getBoundingClientRect()
  const x = groupRect.left + group.clientLeft - group.scrollLeft
  const y = groupRect.top + group.clientTop - group.scrollTop
  const rtlOverflow = rtl ? width - viewportWidth : 0
  return {
    left: elementRect.left - x + rtlOverflow,
    right: elementRect.right - x + rtlOverflow,
    top: elementRect.top - y,
    bottom: elementRect.bottom - y,
  }
}

function clip(rect: Rect, width: number, height: number, inset = 0, radius = 0) {
  const top = rect.top + inset
  const right = width - rect.right + inset
  const bottom = height - rect.bottom + inset
  const left = rect.left + inset
  return `inset(${top}px ${right}px ${bottom}px ${left}px round ${radius}px)`
}

function insetClip(path: string, amount: number) {
  return path.replace(
    /^inset\(([-\d.]+)px ([-\d.]+)px ([-\d.]+)px ([-\d.]+)px round ([-\d.]+)px\)$/,
    (_match, top, right, bottom, left, radius) =>
      `inset(${Number(top) + amount}px ${Number(right) + amount}px ${Number(bottom) + amount}px ${Number(left) + amount}px round ${Math.max(0, Number(radius) - amount)}px)`,
  )
}

function box(rect: Rect, width: number, height: number, kind: Kind): Rect {
  return kind === 'underline'
    ? { left: rect.left, right: rect.right, top: rect.bottom - 3, bottom: rect.bottom - 1 }
    : {
        left: Math.max(0, rect.left),
        right: Math.min(width, rect.right),
        top: Math.max(0, rect.top),
        bottom: Math.min(height, rect.bottom),
      }
}

function keyframes(
  from: Position,
  to: Position,
  width: number,
  height: number,
  rtl: boolean,
  kind: Kind,
) {
  const start = box(from.rect, width, height, kind)
  const end = box(to.rect, width, height, kind)
  const forward =
    to.index > from.index ||
    (to.index === from.index &&
      (rtl ? to.rect.left < from.rect.left : to.rect.left > from.rect.left))
  if (forward) {
    const middle = {
      left: rtl ? end.left : start.left,
      right: rtl ? start.right : end.right,
      top: Math.min(start.top, end.top),
      bottom: Math.max(start.bottom, end.bottom),
    }
    return [
      {
        clipPath: clip(start, width, height, 0, kind === 'segmented' ? 3 : 0),
        easing: sigEase('--sig-ease-write'),
      },
      {
        clipPath: clip(middle, width, height, 0, kind === 'segmented' ? 3 : 0),
        offset: 0.55,
        easing: sigEase('--sig-ease-write'),
      },
      { clipPath: clip(end, width, height, 0, kind === 'segmented' ? 3 : 0) },
    ]
  }

  const oldEnd = rtl ? { ...start, right: start.left } : { ...start, left: start.right }
  const newStart = rtl ? { ...end, left: end.right } : { ...end, right: end.left }
  return [
    {
      clipPath: clip(start, width, height, 0, kind === 'segmented' ? 3 : 0),
      easing: sigEase('--sig-ease-write'),
    },
    { clipPath: clip(oldEnd, width, height, 0, kind === 'segmented' ? 3 : 0), offset: 0.4 },
    {
      clipPath: clip(newStart, width, height, 0, kind === 'segmented' ? 3 : 0),
      offset: 0.401,
      easing: sigEase('--sig-ease-write'),
    },
    { clipPath: clip(end, width, height, 0, kind === 'segmented' ? 3 : 0) },
  ]
}

/** Replays a changed tab/segment mark without changing the control's final state. */
export function useDirectionalIndicator<T extends HTMLElement>(
  ref: RefObject<T | null>,
  value: string | null,
  selector: string,
  kind: Kind,
) {
  const previous = useRef<Snapshot | null>(null)
  const stopCurrent = useRef<Stop | null>(null)

  useLayoutEffect(() => {
    const group = ref.current
    if (!group) return

    const groupRect = group.getBoundingClientRect()
    const viewportWidth = group.clientWidth || groupRect.width
    const width = Math.max(group.clientWidth, group.scrollWidth, viewportWidth)
    const height = group.clientHeight || groupRect.height
    const rtl = directionOf(group)

    const items = Array.from(group.children).filter(
      (child): child is HTMLElement =>
        child instanceof HTMLElement &&
        child.matches(
          kind === 'underline' ? '[role="tab"]' : '[data-slot="segment"], [role="tab"]',
        ),
    )
    const selected = value === null ? undefined : items.find((item) => item.matches(selector))
    const index = selected ? items.indexOf(selected) : -1
    const next =
      selected && index >= 0
        ? {
            value: value as string,
            index,
            rect: relativeRect(group, selected, rtl, width, viewportWidth),
            element: selected,
          }
        : null
    const before = previous.current

    if (!next) {
      stopCurrent.current?.(true)
      previous.current = null
      return
    }
    previous.current = next
    if (!before || before.value === next.value) return

    stopCurrent.current?.(true)
    if (!signatureMoves()) return

    const beforeIndex = items.indexOf(before.element)
    const oldPosition =
      beforeIndex >= 0
        ? {
            index: beforeIndex,
            rect: relativeRect(group, before.element, rtl, width, viewportWidth),
          }
        : { index: before.index, rect: before.rect }
    if (!width || !height || !oldPosition.rect.right || !next.rect.right) return

    const oldRect = oldPosition
    const newRect = { rect: next.rect, index: next.index }
    const frames = keyframes(oldRect, newRect, width, height, rtl, kind)
    const overlay = document.createElement('span')
    overlay.dataset.slot = 'signature-indicator'
    overlay.setAttribute('aria-hidden', 'true')
    Object.assign(overlay.style, {
      position: 'absolute',
      insetBlockStart: '0',
      insetInlineStart: rtl ? 'auto' : '0',
      insetInlineEnd: rtl ? '0' : 'auto',
      width: `${width}px`,
      height: `${height}px`,
      pointerEvents: 'none',
      zIndex: '0',
    })
    const targets: HTMLElement[] = []
    if (kind === 'underline') {
      overlay.style.background = 'var(--brand)'
      targets.push(overlay)
    } else {
      for (const color of ['var(--input)', 'var(--card)']) {
        const layer = document.createElement('span')
        Object.assign(layer.style, {
          position: 'absolute',
          insetBlock: '0',
          insetInline: '0',
          background: color,
        })
        overlay.appendChild(layer)
        targets.push(layer)
      }
    }

    const oldHidden = selected?.getAttribute('data-signature-indicator-hidden') ?? null
    selected?.setAttribute('data-signature-indicator-hidden', 'true')
    group.appendChild(overlay)
    const duration = sigMs('--sig-dur-indicator', 380)
    const animations = targets.map((target, index) => {
      const layerFrames =
        kind === 'segmented' && index === 1
          ? frames.map((frame) => ({ ...frame, clipPath: insetClip(String(frame.clipPath), 1) }))
          : frames
      return target.animate(layerFrames, {
        duration,
        easing: sigEase('--sig-ease-write'),
        fill: 'both',
      })
    })
    let timer = 0
    let stopped = false
    let finishWhenHidden = () => {}
    const stop: Stop = (cancel) => {
      if (stopped) return
      stopped = true
      window.clearTimeout(timer)
      document.removeEventListener('visibilitychange', finishWhenHidden)
      if (cancel) {
        animations.forEach((animation) => {
          animation.cancel()
        })
      }
      overlay.remove()
      if (oldHidden === null) selected?.removeAttribute('data-signature-indicator-hidden')
      else selected?.setAttribute('data-signature-indicator-hidden', oldHidden)
      if (stopCurrent.current === stop) stopCurrent.current = null
    }
    const finish = () => {
      for (const animation of animations) {
        if (animation.playState === 'idle' || animation.playState === 'finished') continue
        try {
          animation.finish()
        } catch {
          animation.cancel()
        }
      }
      stop(false)
    }
    finishWhenHidden = () => {
      if (document.hidden) finish()
    }
    stopCurrent.current = stop
    Promise.all(animations.map((animation) => animation.finished.catch(() => undefined))).then(() =>
      stop(false),
    )
    timer = window.setTimeout(finish, duration + 120)
    document.addEventListener('visibilitychange', finishWhenHidden)
  })

  useEffect(() => () => stopCurrent.current?.(true), [])
}
